/* eslint-disable @nx/enforce-module-boundaries */
import {
  CheckGreen,
  DeleteIcon,
  ExclamationIcon,
  UploadModalHeaderIconLg,
} from '@mybridge/icons';
import {
  Box,
  Button,
  HStack,
  Heading,
  IconButton,
  Input,
  Link,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Stack,
  Text,
  Tooltip,
  useToast,
} from '@mybridge/ui';
import { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  addFilesToUpload,
  getfileNFolder,
  removeFilesToUpload,
  setFilesToUpload,
  updateFileToUpload,
} from 'v4/store/actions/myDisk.Action';
import { APIEndPoints } from 'v4/store/common/endPoint';
import { DiskContext } from '../context';
import { FileFolderIcon } from '../my-disk/file-icon';
import {
  calculateChecksum,
  getFileExtension,
  getFileName,
  trimStr,
} from 'v4/lib/commons';
import { File } from 'buffer';
import { uniqueId } from 'lodash';
import { isMobile } from 'react-device-detect';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { useDirection } from '@mybridge/ui/custom-hooks/useDirection';
import { useMultipartUpload } from 'packages/mybridgev4/hooks/useMultipartUpload';

/**
 * Simple file upload modal for uploading multiple files (sequentially)\
 * @param {*} props
 * @returns
 */
export const FileUploadModal = ({ ...props }) => {
  const { filesToUpload: files } = useSelector((state) => state.myDisk);
  const {
    team,
    uploadDisc,
    selectedDir,
    refetchFiles,
    refetchMyDiskOccupiedSize,
    selectedMainDir,
  } = useContext(DiskContext);
  const toast = useToast();
  const [dragHover, setDragHover] = useState(false);
  // const [files, setFiles] = useState([])
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslator();
  const direction = useDirection();
  const id = useRef(uniqueId('dnd-'));
  const tid = useRef(-1);
  const filesRef = useRef(files);
  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  const { uploadFile: uploadMultipartFileItem } = useMultipartUpload({
    uploadSource: 'disk',
    onProgress: ({ file: uploadingFile, percentage }) => {
      const currentFiles = filesRef.current || [];
      const fileIdx = currentFiles.findIndex(
        (f) => f.name === uploadingFile?.name && f.size === uploadingFile?.size
      );
      if (fileIdx > -1) {
        dispatch(
          updateFileToUpload({
            index: fileIdx,
            json: { loaded: percentage },
          })
        );
      }
    },
  });
  useEffect(() => {
    // setup event bindings
    clearTimeout(tid.current);
    tid.current = setTimeout(() => {
      if (uploadDisc?.isOpen) {
        bindDnDAreaEvents();
      }
    }, 100);
    return () => unbindDnDAreaEvents();
  }, [uploadDisc?.isOpen]);

  // listen to file changes and set uploading state properly
  useEffect(() => {
    if (files && files.length) {
      if (!isUploadPending()) {
        setIsUploading(false);
      }
    }
  }, [files]);

  /**
   * Bind drag/drop events to dndArea
   */
  const bindDnDAreaEvents = () => {
    // eslint-disable-next-line valid-typeof
    if (typeof window !== undefined) {
      const dndAreaElement = document.querySelector('#' + id?.current);
      if (dndAreaElement) {
        dndAreaElement.addEventListener('dragenter', handleDragEnter);
        dndAreaElement.addEventListener('dragover', handleDragOver);
        dndAreaElement.addEventListener('dragleave', handleDragLeave);
        dndAreaElement.addEventListener('drop', handleDrop);
      }
    }
  };

  /**
   * Unbind drag/drop events from dndArea
   */
  const unbindDnDAreaEvents = () => {
    // eslint-disable-next-line valid-typeof
    if (typeof window !== undefined) {
      const dndAreaElement = document.querySelector('#' + id?.current);
      if (dndAreaElement) {
        dndAreaElement.removeEventListener('dragenter', handleDragEnter);
        dndAreaElement.removeEventListener('dragover', handleDragOver);
        dndAreaElement.removeEventListener('dragleave', handleDragLeave);
        dndAreaElement.removeEventListener('drop', handleDrop);
      }
    }
  };

  /**
   * Handle drag enter event on the dnd area
   * @param {*} e
   */
  const handleDragEnter = (e) => {
    preventDefaults(e);
    setDragHover(true);
  };

  /**
   * Handle drag over event on the dnd area
   * @param {*} e
   */
  const handleDragOver = (e) => {
    preventDefaults(e);
    setDragHover(true);
  };

  /**
   * Handle drag leave event on the dnd area
   * @param {*} e
   */
  const handleDragLeave = (e) => {
    preventDefaults(e);
    setDragHover(false);
  };

  /**
   * Handle drop event on the dnd area
   * @param {*} e
   */
  const handleDrop = (e) => {
    preventDefaults(e);
    setDragHover(false);
    if (e.dataTransfer?.files) {
      handleFiles(e.dataTransfer.files);
    }
  };

  /**
   * Prevent default actions for any kind of event
   * @param {*} e
   */
  const preventDefaults = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  /**
   * Opens operating system's file selection dialog
   * @param {*} e
   */
  const triggerFileUploadSelector = (e) => {
    if (fileInputRef && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  /**
   * Handle file selection by click
   * @param {*} e
   */
  const onFileInputChange = (e) => {
    preventDefaults(e);
    setDragHover(false);
    if (e?.target?.files) {
      handleFiles(e?.target?.files);
    }
  };

  /**
   * Handle selected files
   * @param {*} selectedFiles
   */
  const handleFiles = (selectedFiles) => {
    if (selectedFiles && selectedFiles.length) {
      dispatch(addFilesToUpload(selectedFiles));
    }
  };

  /**
   * Remove file from uploading list (before upload begins)
   * @param {*} fileIndex
   */
  const removeFile = (fileIndex) => {
    if (files && files.length && fileIndex > -1 && fileIndex < files.length) {
      dispatch(removeFilesToUpload([fileIndex]));
      fileInputRef.current.value = '';
    }
  };

  /**
   * Upload using XMLHttpReq
   * @param {*} file
   */
  const xUpload = async (payload, index) => {
    return new Promise((res, rej) => {
      try {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', process.env.BASE_API_URL + APIEndPoints.MyDisk_Files);
        xhr.setRequestHeader(
          'Authorization',
          'Bearer ' + localStorage.getItem('access')
        );
        // xhr.setRequestHeader("content-type", 'multipart/form-data; boundary=63c5979328c44e2c869349443a94200e')
        if (xhr.upload) {
          xhr.upload.onprogress = function (e) {
            dispatch(
              updateFileToUpload({
                index,
                json: { loaded: ((e.loaded ?? 0) / (e.total ?? 1)) * 100 },
              })
            );
          };
        }

        // eslint-disable-next-line @typescript-eslint/no-empty-function
        xhr.onload = function () {};

        xhr.onerror = function (err) {
          console.error('error');
          rej(err);
        };
        
        xhr.onloadend = function () {
          console.error('done');
          res();
        };
        
        xhr.onreadystatechange = function (e) {
         if (e.target?.readyState === 4 && e.target?.status >= 400) {
            try {
              let error = {};
              if (e.target?.responseText) {
                error = JSON.parse(e.target.responseText);
              }
              const msg = error?.message || error?.file?.[0] || "Upload failed!";
              rej(msg);
            } catch (err) {
              rej("Upload failed!");
            }
          }
        };
        
        xhr.send(payload);
      } catch (e) {
        rej(e);
      }
    });
  };

  /**
   * Actually upload file
   * @param {*} file
   * @param {*} index
   */
  const upload = async (file, index) => {
    const extn_ = getFileExtension(file?.name);
    // const name_ = trimStr(getFileName(file?.name), { length: 10 });
    const name_ = getFileName(file?.name);
    if (selectedDir?.id) {
      dispatch(updateFileToUpload({ index, json: { status: 'uploading' } }));
      try {
        const res = await uploadMultipartFileItem(file, 'disk');
        const uploadId = res?.uploadId || res?.data?.upload_id;

        let payload = new FormData();
        if (uploadId) {
          payload.append('upload_ids', uploadId);
        } else {
          // payload.append('file', file, `${name_}.${extn_}`);
          payload.append('file', file, `${name_}.${extn_}`);
        }
        payload.append('parent', selectedDir?.id);
        if (team?.id) {
          payload.append('team_ref', team?.id);
        } else if (selectedMainDir?.team_info?.id) {
          payload.append('team_ref', selectedMainDir?.team_info?.id);
        }

        await xUpload(payload, index);
        dispatch(updateFileToUpload({ index, json: { status: 'done' } }));
      } catch (errorMessage) {
        const errText = typeof errorMessage === 'string' ? errorMessage : errorMessage?.message || "Upload failed!";
        toast({
          title: errText,
          status: "error",
          position: "top",
          duration: 5000,
          isClosable: true,
        });
        dispatch(
          updateFileToUpload({ index, json: { status: 'error', errorMessage: errText } })
        );
      }
    } else {
      throw new Error('Invalid parent directory!');
    }
  };

  /**
   * Start uploading files one by one
   */
  const startUploading = async () => {
    if (files && files.length > 0) {
      setIsUploading(true);
      try {
        for (let index in files ?? []) {
          const file = files[index];
          if (file.status === 'done' || file.state === 'error') continue;
          try {
            const checksum = await calculateChecksum(file, 10000);
            dispatch(updateFileToUpload({ index, json: { checksum } }));
            await upload(file, index);
            // refetchFiles?.();
            // refetchMyDiskOccupiedSize?.();
            // handleOnClose()
          } catch (e) {
            console.error('Error', e);
            file.status = 'error';
            file.errorMessage = e.message;
            dispatch(
              updateFileToUpload({
                index,
                json: { status: 'error', errorMessage: e.message },
              })
            );
          }
        }
        // All files done — refetch and close modal once
        refetchFiles?.();
        refetchMyDiskOccupiedSize?.();
        handleOnClose();
      } catch (e) {
        console.error(e);
      }
    }
  };

  /**
   * Check if all the files in the list have been uploaded
   */
  const isUploadPending = () => {
    return (
      files?.filter?.((f) => f.status !== 'done' && f.status !== 'error')
        ?.length > 0
    );
  };

  const isFilesUploadPending = () => {
    return files?.filter?.((f) => f.status === 'pending')?.length > 0;
  };

  const handleOnClose = () => {
    // if (isUploadPending()) {
    //   if (typeof window !== 'undefined') {
    //     const confirm = window.confirm(
    //       'Changes will be lost! Press OK to continue...'
    //     );
    //     if (!confirm) return;
    //   }
    // }
    dispatch(setFilesToUpload([]));
    if (!isUploadPending()) {
      dispatch(getfileNFolder({ parent: selectedDir?.id }));
    }
    uploadDisc?.onClose?.();
  };

  const undoneFiles = files?.filter?.((f) => isUploadPending(f));

  return (
    <Modal size= {isMobile ? "sm": "2xl"} {...uploadDisc} onClose={handleOnClose} closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent borderRadius="18px">
        <ModalCloseButton {...(direction === 'rtl') ? {left: '0.75rem', right: 'auto'} : {left: 'auto', right: '0.75rem'}} />
        <ModalHeader color="blackAlpha.900" fontSize="md">
          {t('myDisk.uploadFiles') || "Upload files"}
        </ModalHeader>
        <ModalBody>
          {isFilesUploadPending?.() ? (
            ''
          ) : (
            <Stack
              id={id.current}
              className="drag__area"
              justifyContent="center"
              alignItems="center"
              border="1px dashed"
              borderColor="blackAlpha.500"
              borderRadius="18px"
              onClick={triggerFileUploadSelector}
              _hover={{ bg: 'gray.50' }}
            >
              <Input
                multiple
                onChange={onFileInputChange}
                type="file"
                ref={fileInputRef}
                display="none"
              />
              <Stack p={6} alignItems="center">
                <UploadModalHeaderIconLg />
                {/* <Heading size="md">Drag n' Drop Here</Heading> */}
                <Box>
                  <Text display="inline">{t('myDisk.dragAndDrop') || "Drag and drop or your files"}</Text>
                  <Text
                    display="inline"
                    color="brandPrimary.500"
                    fontWeight="medium"
                  >
                    {' '}
                    {t('myDisk.browse') || "browse"}{' '}
                  </Text>
                  <Text display="inline">{t('myDisk.yourFiles') || "your files"}</Text>
                </Box>
              </Stack>
            </Stack>
          )}
          <Stack py={4}>
            {files?.length ? (
              <List>
                {files?.map((file, findex) => (
                  <Link key={findex}>
                    <ListItem
                      borderRadius={5}
                      _hover={{ bg: 'brandGray.500' }}
                      p={2}
                      as={HStack}
                      role="group"
                    >
                      <HStack flex={1}>
                        <FileFolderIcon file={file} width={20} height={20} />
                        <Text truncate={true} maxW="64">
                          {file?.name}
                        </Text>
                      </HStack>
                      <Stack w="30%" rounded='full' overflow='hidden'>
                        <Progress
                          colorScheme={
                            file?.status === 'error' ? 'red' : 'brandPrimary'
                          }
                          value={
                            file?.status === 'error'
                              ? 100
                              : file?.status === 'done'
                              ? 100
                              : file?.loaded ?? 0
                          }
                        />
                      </Stack>
                      <HStack w="15%" justifyContent="flex-end">
                        {!isUploading && file?.status !== 'done' ? (
                          <IconButton
                            visibility="hidden"
                            _groupHover={{ visibility: 'visible' }}
                            onClick={(e) => removeFile(findex)}
                            variant="ghost"
                          >
                            <DeleteIcon />
                          </IconButton>
                        ) : (
                          ''
                        )}
                        {file.status === 'done' ? (
                          <IconButton variant="ghost">
                            <CheckGreen style={{ width: '18px' }} />
                          </IconButton>
                        ) : (
                          ''
                        )}
                        {file.status === 'error' ? (
                          <Tooltip label={file?.errorMessage}>
                            <IconButton
                              // visibility="hidden"
                              // _groupHover={{ visibility: 'visible' }}
                              variant="ghost"
                            >
                              <ExclamationIcon width={18} height={18} />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <></>
                        )}
                      </HStack>
                    </ListItem>
                  </Link>
                ))}
              </List>
            ) : (
              ''
            )}
          </Stack>
        </ModalBody>
        {files?.length ? (
          <ModalFooter>
            {undoneFiles?.length ? (
              <Button
                onClick={startUploading}
                isLoading={isUploading}
                isDisabled={files?.length <= 0}
                variant="primary"
                borderRadius="30px"
              >
                {t('myDisk.submit') || "Submit"}
              </Button>
            ) : undoneFiles?.length ? (
              <Button borderRadius="30px" onClick={(e) => handleOnClose?.()} variant="secondary">
                {t('publicProfile.close') || "Close"}
              </Button>
            ) : (
              ''
            )}
          </ModalFooter>
        ) : (
          ''
        )}
      </ModalContent>
    </Modal>
  );
};
